import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Modal } from 'react-bootstrap';
import { getAgents, createAgent, updateAgent } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const Agents = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        agentId: ''
    });
    const [editingAgent, setEditingAgent] = useState(null);
    const [processing, setProcessing] = useState(false);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            const response = await getAgents();
            
            if (response.success && response.data) {
                // Handle different response structures
                const agentsData = response.data.agents || response.data || [];
                setAgents(Array.isArray(agentsData) ? agentsData : []);
            } else {
                setAgents([]);
                if (response.message) {
                    setError(response.message);
                }
            }
        } catch (error) {
            console.error('Error fetching agents:', error);
            setError(error.message || 'Failed to fetch agents');
            setAgents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAgents();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);
            if (editingAgent) {
                await updateAgent(editingAgent._id, formData);
                alert('Agent updated successfully');
            } else {
                await createAgent({ ...formData, status: 'active' });
                alert('Agent created successfully');
            }
            setShowModal(false);
            setFormData({ agentId: '' });
            setEditingAgent(null);
            fetchAgents();
        } catch (error) {
            alert(error.message || 'Failed to save agent');
        } finally {
            setProcessing(false);
        }
    };

    const handleEdit = (agent) => {
        setEditingAgent(agent);
        setFormData({
            agentId: agent.agentId || ''
        });
        setShowModal(true);
    };

    const handleStatusUpdate = async (id, newStatus) => {
        const action = newStatus === 'active' ? 'activate' : 'deactivate';
        const confirmed = window.confirm(
            `Are you sure you want to ${action} this agent?`
        );

        if (!confirmed) return;

        try {
            setProcessing(true);
            const response = await updateAgent(id, { status: newStatus });

            if (response.success) {
                alert('Agent status updated successfully');
                fetchAgents();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert(error.message || 'Failed to update status');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            inactive: 'danger'
        };
        return badges[status] || 'secondary';
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Agents" sidebutton={true} />
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <Card.Title>Agents</Card.Title>
                            <Button variant="primary" onClick={() => {
                                setEditingAgent(null);
                                setFormData({ agentId: '' });
                                setShowModal(true);
                            }}>
                                Add Agent
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <div className="table-responsive">
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Agent ID</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!Array.isArray(agents) || agents.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="text-center">No agents found</td>
                                            </tr>
                                        ) : (
                                            agents.map((agent) => (
                                                <tr key={agent._id || agent.id}>
                                                    <td>{agent.agentId || '-'}</td>
                                                    <td>
                                                        <Badge bg={getStatusBadge(agent.status)}>
                                                            {agent.status ? agent.status.charAt(0).toUpperCase() + agent.status.slice(1) : '-'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant={agent.status === 'active' ? 'warning' : 'success'}
                                                            size="sm"
                                                            onClick={() => handleStatusUpdate(agent._id || agent.id, agent.status === 'active' ? 'inactive' : 'active')}
                                                            disabled={processing}
                                                            className="me-2"
                                                        >
                                                            <i className={`fa ${agent.status === 'active' ? 'fa-ban' : 'fa-check'}`}></i>
                                                            {agent.status === 'active' ? ' Deactivate' : ' Activate'}
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => handleEdit(agent)}
                                                            disabled={processing}
                                                        >
                                                            <i className="fa fa-edit"></i> Edit
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingAgent ? 'Edit Agent' : 'Add Agent'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Agent ID</Form.Label>
                            <Form.Control
                                type="text"
                                name="agentId"
                                value={formData.agentId}
                                onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={processing}>
                            {processing ? 'Processing...' : (editingAgent ? 'Update' : 'Create')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default Agents;

